function countOccurrences(text, substring) {
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] == substring) {
      count += 1;
    }
  }
  return count;
}

export function validateEmail(email) {
  // we want Rubicon users to be able to register addresses like abc+x@tryrubicon.com
  if (email.endsWith("@tryrubicon.com") && countOccurrences(email, "@") == 1) {
    return true;
  }
  return /^[\w&]+([.-]?[\w&]+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email);
}

export function isRubiconInternalEmail(email?: string | null): boolean {
  return !!email && email.toLowerCase().endsWith("@tryrubicon.com");
}

export function validatePhone(phone) {
  return /^(\d{10}|\d{11})$/.test(phone);
}

export const formatPhoneNumber = (phoneNumber) => {
  const digits = phoneNumber.replace(/\D/g, "");

  if (digits.length < 4) return digits;
  if (digits.startsWith("1") && digits.length < 5)
    return `+1 (${digits.slice(1)}`;

  if (digits.startsWith("1") && digits.length <= 11) {
    return `+1 (${digits.slice(1, 4)}) ${
      digits.length < 8
        ? digits.slice(4)
        : `${digits.slice(4, 7)}-${digits.slice(7)}`
    }`;
  } else if (digits.length <= 10) {
    return `(${digits.slice(0, 3)}) ${
      digits.length < 7
        ? digits.slice(3)
        : `${digits.slice(3, 6)}-${digits.slice(6)}`
    }`;
  }

  return digits;
};

export const stripPhoneNumberFormatting = (phoneNumber) =>
  phoneNumber.replace(/\D/g, "");

export const isSmallScreen = () => {
  return window.innerWidth <= 600;
};

export const formatDateLong = (dateTime, locale = "en") => {
  const m = new Date(dateTime);
  return m.toLocaleDateString(locale, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  });
};

export const formatDateShort = (dateTime, locale = "en") => {
  const m = new Date(dateTime);
  return m.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  });
};

export const formatWeekdayShort = (dateTime, locale = "en") => {
  const m = new Date(dateTime);
  return m.toLocaleDateString(locale, { weekday: "short" });
};

/**
 * "Monday, Jan 14, 2025 · 14:23" — an incident time as a bulletin states it.
 *
 * Deliberately not `formatDateLong`, which the same page uses for *Issued on*. The two are read for
 * different things: an incident time is a fact to match an occurrence against, so it is short-month
 * and 24-hour the way a police service writes one, while the issue date is prose and stays long.
 */
export const formatIncidentDateTime = (dateTime, locale = "en") => {
  const m = new Date(dateTime);
  const date = m.toLocaleDateString(locale, {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = m.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${date} · ${time}`;
};

/** The same date with no time, for an incident whose time of day is not known. */
export const formatIncidentDate = (dateTime, locale = "en") =>
  new Date(dateTime).toLocaleDateString(locale, {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export const formatDateWithoutTime = (dateTime, locale = "en") => {
  const m = new Date(dateTime);
  return m.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Converts a Date String (YYYY-MM-DD) into
 * proper timezone ISO String (YYYY-MM-DDTHH:mm.ss.xxxZ)
 */
export const dateToISOString = function (date: string): string {
  date = date + "T00:00:00";
  return new Date(date).toISOString();
};

export const formatDateForPDF = (
  date: string,
  locale: string = "en",
  dateOnly: boolean = false,
) => {
  const dateObj = new Date(date);
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const dayName = days[dateObj.getDay()];

  if (dateOnly) {
    const dateString = dateObj.toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "2-digit",
    });
    return `${dayName} - ${dateString}`;
  }

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  const dateString = dateObj.toLocaleDateString(locale, options);
  const [monthDayYear, time] = dateString.split(", ");

  return `${dayName} - ${monthDayYear}, ${time}`;
};

export const mailTo = ({ email, subject, body }) => {
  return `mailto:${email}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
};

export const localeToLanguage = {
  en: "English",
  '"fr"': "French",
  fr: "French",
  frCa: "French (Canada)",
  es: "Spanish",
  hi: "Hindi",
  de: "German",
  ru: "Russian",
  ar: "Arabic",
  it: "Italian",
  zh: "Chinese",
  ko: "Korean",
  ja: "Japanese",
  he: "Hebrew",
  pa: "Punjabi",
  gu: "Gujarati",
  tl: "Tagalog",
  pl: "Polish",
  pt: "Portuguese",
  vi: "Vietnamese",
  ur: "Urdu",
  so: "Somali",
  iu: "Inuktitut",
};

export interface SupportedLanguage {
  code: string;
  name: string;
}

export const supportedLanguages: SupportedLanguage[] = [
  { code: "ar", name: "Arabic" },
  { code: "zh", name: "Chinese" },
  { code: "en", name: "English" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "he", name: "Hebrew" },
  { code: "hi", name: "Hindi" },
  { code: "it", name: "Italian" },
  { code: "ja", name: "Japanese" },
  { code: "ko", name: "Korean" },
  { code: "ru", name: "Russian" },
  { code: "es", name: "Spanish" },
  { code: "pa", name: "Punjabi" },
  { code: "gu", name: "Gujarati" },
  { code: "tl", name: "Tagalog" },
  { code: "pl", name: "Polish" },
  { code: "pt", name: "Portuguese" },
  { code: "vi", name: "Vietnamese" },
  { code: "ur", name: "Urdu" },
  { code: "so", name: "Somali" },
  { code: "iu", name: "Inuktitut" },
];
